import React, { useState, useEffect, useCallback } from 'react';
import ScSpinner from '../ScSpinner';
import {
  fetchProjectVisitSelections,
  saveProjectVisitSelections,
} from '../../studyConfigurationApi';

/**
 * Study Configuration — assign which global visit types apply to the session project.
 * Locked after the first site is created for the project.
 */
const ProjectVisitTypesTab = ({ projectCode }) => {
  const [items, setItems] = useState([]);
  const [selectedNos, setSelectedNos] = useState([]);
  const [locked, setLocked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const load = useCallback(async () => {
    if (!projectCode) {
      setItems([]);
      setSelectedNos([]);
      setLocked(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const res = await fetchProjectVisitSelections(projectCode);
      const next = res.data?.items || [];
      setItems(next);
      setSelectedNos(next.filter((i) => i.isSelected).map((i) => i.studyVisitTypeNo));
      setLocked(!!res.data?.locked);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load project visit assignment.');
      setItems([]);
      setSelectedNos([]);
      setLocked(false);
    } finally {
      setLoading(false);
    }
  }, [projectCode]);

  useEffect(() => {
    setSuccess(null);
    load();
  }, [load]);

  const toggle = (studyVisitTypeNo) => {
    if (locked) return;
    setSelectedNos((prev) =>
      prev.includes(studyVisitTypeNo)
        ? prev.filter((n) => n !== studyVisitTypeNo)
        : [...prev, studyVisitTypeNo]
    );
  };

  const save = async () => {
    if (!projectCode || locked) return;
    if (selectedNos.length === 0) {
      setError('Select at least one visit type for the project.');
      setSuccess(null);
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await saveProjectVisitSelections({
        projectCode,
        studyVisitTypeNos: selectedNos,
        baseProfileCode: 'CRA',
      });
      setSuccess('Project visit assignment saved. New sites will inherit these visit types.');
      await load();
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to save project visit assignment.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <ScSpinner />;

  return (
    <div className="sc-project-visit-types">
      {error && <div className="sc-alert sc-alert-error">{error}</div>}
      {success && <div className="sc-alert sc-alert-success">{success}</div>}
      <p className="sc-empty" style={{ fontStyle: 'normal', marginBottom: '0.75rem' }}>
        Choose which global visit types this project uses. Sites cannot be created until at least one
        type is saved. New sites inherit this list; use Skip Visit Management to uncheck types per
        site before any visit is scheduled.
      </p>
      {locked && (
        <div className="sc-alert sc-alert-info" style={{ marginBottom: '0.75rem' }}>
          Project visit types are locked because a site already exists for this project.
        </div>
      )}

      {items.length === 0 ? (
        <p className="sc-empty">
          No active visit types in the global master. Add them under Global → Visit Types.
        </p>
      ) : (
        <>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 10,
              marginBottom: '1rem',
            }}
          >
            {items.map((vt) => (
              <label
                key={vt.studyVisitTypeNo}
                style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}
              >
                <input
                  type="checkbox"
                  checked={selectedNos.includes(vt.studyVisitTypeNo)}
                  onChange={() => toggle(vt.studyVisitTypeNo)}
                  disabled={locked}
                />
                <span>
                  <strong>{vt.visitCode}</strong> — {vt.visitName}
                  {vt.beforeOnboarding ? ' (before onboarding)' : ''}
                  {vt.studyDesignDesc ? ` · ${vt.studyDesignDesc}` : ''}
                </span>
              </label>
            ))}
          </div>
          <div className="sc-toolbar">
            <button
              type="button"
              className="sc-btn sc-btn-primary"
              onClick={save}
              disabled={saving || locked}
            >
              {saving ? 'Saving…' : 'Save project visit types'}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default ProjectVisitTypesTab;
